import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import * as navigationStore from '@cartwhl/common/stores/navigationStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/Text';
import BlueDotIcon from '@cartwhl/ui/icons/BlueDotIcon';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import LightDarkIcon from '@cartwhl/ui/icons/LightDarkIcon';
import LikeIcon from '@cartwhl/ui/icons/LikeIcon';
import LogoutIcon from '@cartwhl/ui/icons/LogoutIcon';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import UserIcon from '@cartwhl/ui/icons/UserIcon';

interface UserMenuProps {
  inModal?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export default function UserMenu({
  inModal = false,
  side = 'bottom',
  align = 'end',
}: UserMenuProps) {
  const { userInfo, theme } = useSnapshot(userStore.state);

  return (
    <Dropdown
      inModal={inModal}
      side={side}
      align={align}
      trigger={
        <div className="hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 outline-hidden flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors">
          <Avatar userInfo={userInfo} />
        </div>
      }
    >
      <div className="hover:bg-gray-15 hover:dark:bg-gray-90 m-2 h-[120px] rounded-md">
        <a
          className="relative flex h-full w-full flex-col items-center justify-center"
          href={getHomepageUrl('/profile/account-details')}
        >
          <Avatar userInfo={userInfo} size={12} className="relative mb-3">
            <span className="dark:bg-gray-90 absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white">
              <PenIcon />
            </span>
          </Avatar>
          <Text variant="studio/body/medium/sm" className="mb-2.5 ">
            {userInfo.firstName} {userInfo.lastName}
          </Text>
          <Text variant="studio/body/xs" className="text-gray-45">
            {userInfo.email}
          </Text>
        </a>
      </div>
      <div className="flex items-center justify-center">
        <a
          href={getHomepageUrl('/profile/plan-billing')}
          className="bg-yellow inline-flex items-center gap-1 rounded-md px-3 py-2 text-black hover:bg-[#E5F402]"
        >
          <Text variant="studio/body/sm">
            {userInfo.planType === 'unlimited' ? '∞' : userInfo.spinsAvailable}{' '}
            Spins
          </Text>
        </a>
      </div>

      <div className="mt-2 flex items-center justify-center">
        <Link
          className="text-gray-45 cursor-pointer underline"
          onClick={() => {
            navigationStore.setModal(
              navigationStore.CommonModalName.EXPLAIN_SPINS,
            );
          }}
        >
          <Text variant="studio/body/xs" className="">
            What are Spins?
          </Text>
        </Link>
      </div>

      <Dropdown.Separator />

      {userInfo.planType !== 'unlimited' && (
        <>
          <Dropdown.Item
            href={getHomepageUrl('/pricing')}
            icon={
              <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
            }
            variant="blue"
          >
            {userInfo.planType === 'free'
              ? 'Upgrade'
              : userInfo.planType === 'pro'
                ? 'Upgrade to Unlimited'
                : 'Unlimited Plan'}
          </Dropdown.Item>

          <Dropdown.Separator />
        </>
      )}

      <Dropdown.Item
        icon={<LikeIcon />}
        href={getHomepageUrl('/profile/favorites')}
      >
        Favorites
      </Dropdown.Item>
      <Dropdown.Sub text="Theme" icon={<LightDarkIcon />}>
        <Dropdown.Item
          icon={theme === 'light' ? <CheckmarkIcon /> : null}
          onClick={() => userStore.setTheme('light')}
        >
          Light
        </Dropdown.Item>
        <Dropdown.Item
          icon={theme === 'dark' ? <CheckmarkIcon /> : null}
          onClick={() => userStore.setTheme('dark')}
        >
          Dark
        </Dropdown.Item>
        <Dropdown.Item
          icon={theme === null ? <CheckmarkIcon /> : null}
          onClick={() => userStore.setTheme(null)}
        >
          System
        </Dropdown.Item>
      </Dropdown.Sub>

      <Dropdown.Item
        href={getHomepageUrl('/profile/account-details')}
        icon={<UserIcon />}
      >
        Profile
      </Dropdown.Item>

      <Dropdown.Separator />

      <Dropdown.Item href={getHomepageUrl('/logout')} icon={<LogoutIcon />}>
        Logout
      </Dropdown.Item>
    </Dropdown>
  );
}
